import React, { useEffect, useState } from 'react';
import { mbaAPI } from '../../utils/api';
import MbaProjectForm from '../../components/admin/MbaProjectForm';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import '../../styles/AdminMbaProjects.scss';

const AdminMbaProjects = () => {
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [types, setTypes] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  // Filter states
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [universityId, setUniversityId] = useState('');

  // Bulk update states
  const [bulkTypeId, setBulkTypeId] = useState('');
  const [bulkOriginalPrice, setBulkOriginalPrice] = useState('');
  const [bulkDiscountedPrice, setBulkDiscountedPrice] = useState('');
  const [bulkIsDiscountActive, setBulkIsDiscountActive] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState('');

  const loadCatalog = async () => {
    try {
      const [deptRes, typeRes, uniRes] = await Promise.all([
        mbaAPI.getDepartments(),
        mbaAPI.getTypes(),
        mbaAPI.getUniversities(),
      ]);
      setDepartments(deptRes.data?.data || []);
      setTypes(typeRes.data?.data || []);
      setUniversities(uniRes.data?.data || []);
    } catch (err) {
      setError('Failed to load MBA catalog');
    }
  };

  const loadProjects = async (targetPage = page) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: targetPage,
        limit: PAGE_SIZE,
        sort_by: 'created_at',
        sort_dir: 'asc'
      };

      if (search.trim()) params.search = search.trim();
      if (departmentId) params.department_id = Number(departmentId);
      if (typeId) params.type_id = Number(typeId);
      if (universityId) params.university_id = Number(universityId);

      const response = await mbaAPI.getProjects(params);
      const data = response.data;
      setProjects(data?.data || []);
      setPages(data?.pages || 1);
      setTotal(data?.total || 0);
      setPage(targetPage);
    } catch (err) {
      setError('Failed to load MBA projects');
    } finally {
      setLoading(false);
    }
  };

  // Run catalog load once on mount
  useEffect(() => {
    loadCatalog();
  }, []);

  // Trigger search/filters load with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadProjects(1);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [search, departmentId, typeId, universityId]);

  const handleFormSubmit = async (formData) => {
    if (editingProject) {
      await mbaAPI.updateProject(editingProject.id, formData);
    } else {
      await mbaAPI.createProject(formData);
    }
    await loadProjects(page);
    setShowForm(false);
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this MBA project?')) return;
    try {
      await mbaAPI.deleteProject(id);
      const newItemsLength = projects.length - 1;
      const targetPage = newItemsLength === 0 && page > 1 ? page - 1 : page;
      await loadProjects(targetPage);
    } catch (err) {
      setError('Failed to delete MBA project');
    }
  };

  const handleBulkPriceUpdate = async (e) => {
    e.preventDefault();
    if (!bulkTypeId) {
      alert('Please select a Project Type for bulk update.');
      return;
    }
    const origPrice = Number(bulkOriginalPrice);
    if (isNaN(origPrice) || origPrice <= 0) {
      alert('Original Price must be greater than 0.');
      return;
    }

    let discPrice = null;
    if (bulkIsDiscountActive) {
      discPrice = Number(bulkDiscountedPrice);
      if (isNaN(discPrice) || discPrice >= origPrice || discPrice < 0) {
        alert('Discounted Price must be valid and less than Original Price.');
        return;
      }
    }

    const typeName = types.find(t => t.id === Number(bulkTypeId))?.name || 'selected';
    if (!window.confirm(`Are you sure you want to update prices for ALL projects under "${typeName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkLoading(true);
      setBulkSuccess('');
      setError('');

      const payload = {
        type_id: Number(bulkTypeId),
        originalPrice: origPrice,
        discountedPrice: bulkIsDiscountActive ? discPrice : null,
        isDiscountActive: bulkIsDiscountActive
      };

      await mbaAPI.bulkPriceUpdate(payload);
      setBulkSuccess(`Successfully updated all ${typeName} project prices!`);
      await loadProjects(page);

      // Reset form
      setBulkOriginalPrice('');
      setBulkDiscountedPrice('');
      setBulkIsDiscountActive(false);
      setBulkTypeId('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update bulk prices');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="admin-mba-container">
      {showForm ? (
        <MbaProjectForm
          onSubmit={handleFormSubmit}
          editingProject={editingProject}
          onCancel={() => { setShowForm(false); setEditingProject(null); }}
          departments={departments}
          types={types}
          universities={universities}
          onRefreshCatalog={loadCatalog}
        />
      ) : (
        <>
          <div className="mba-header">
            <div>
              <h2>📘 MBA Projects</h2>
              <p>Manage MBA-specific project listings, documents, and specializations.</p>
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingProject(null);
                setShowForm(true);
              }}
            >
              ➕ Add MBA Project
            </button>
          </div>

          {error && <div className="form-error">{error}</div>}

          {/* ===== SEARCH & FILTERS CARD ===== */}
          <div className="admin-filters-card">
            <h4>🔍 Search & Filter Projects</h4>
            <div className="admin-filters-grid">
              <input
                type="text"
                placeholder="Search by title..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="search-input"
              />
              <select
                value={departmentId}
                onChange={(e) => { setDepartmentId(e.target.value); setPage(1); }}
              >
                <option value="">All Specializations</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
              <select
                value={typeId}
                onChange={(e) => { setTypeId(e.target.value); setPage(1); }}
              >
                <option value="">All Types</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <select
                value={universityId}
                onChange={(e) => { setUniversityId(e.target.value); setPage(1); }}
              >
                <option value="">All Universities</option>
                {universities.map((uni) => (
                  <option key={uni.id} value={uni.id}>{uni.name}</option>
                ))}
              </select>
              <button
                className="btn-clear"
                onClick={() => {
                  setSearch('');
                  setDepartmentId('');
                  setTypeId('');
                  setUniversityId('');
                  setPage(1);
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* ===== BULK PRICE UPDATE CARD ===== */}
          <div className="admin-bulk-card">
            <h4>⚡ Bulk Price Update by Project Type</h4>
            <form onSubmit={handleBulkPriceUpdate} className="bulk-form-grid">
              <select
                value={bulkTypeId}
                onChange={(e) => setBulkTypeId(e.target.value)}
                required
              >
                <option value="">Select Project Type</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Original Price (₹)"
                value={bulkOriginalPrice}
                onChange={(e) => setBulkOriginalPrice(e.target.value)}
                required
                min="0"
              />
              <label className="bulk-checkbox-label">
                <input
                  type="checkbox"
                  checked={bulkIsDiscountActive}
                  onChange={(e) => setBulkIsDiscountActive(e.target.checked)}
                />
                Enable Discount
              </label>
              {bulkIsDiscountActive && (
                <input
                  type="number"
                  placeholder="Discounted Price (₹)"
                  value={bulkDiscountedPrice}
                  onChange={(e) => setBulkDiscountedPrice(e.target.value)}
                  required={bulkIsDiscountActive}
                  min="0"
                />
              )}
              <button type="submit" className="btn-bulk" disabled={bulkLoading}>
                {bulkLoading ? 'Updating...' : 'Apply Bulk Price'}
              </button>
            </form>
            {bulkSuccess && <div className="bulk-success-message">{bulkSuccess}</div>}
          </div>

          <div className="mba-table-card">
            {loading && projects.length === 0 ? (
              <div className="loading">Loading MBA projects...</div>
            ) : projects.length === 0 ? (
              <div className="empty-state">No MBA projects found matching filters.</div>
            ) : (
              <>
                <div className="mba-table">
                  <div className="mba-table-head">
                    <span>SR NO</span>
                    <span>Title</span>
                    <span>Specialization</span>
                    <span>Type</span>
                    <span>University</span>
                    <span>Actions</span>
                  </div>
                  {projects.map((project, index) => (
                    <div key={project.id} className="mba-table-row">
                      <div>{(page - 1) * PAGE_SIZE + index + 1}</div>
                      <div className="title-col">
                        <strong>{project.title}</strong>
                      </div>
                      <div>{project.specialization || '-'}</div>
                      <div>{project.type || '-'}</div>
                      <div>{project.university || '-'}</div>
                      <div className="action-col">
                        <button onClick={() => handleEdit(project)}>Edit</button>
                        <button className="danger" onClick={() => handleDelete(project.id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>

                {pages > 1 && (
                  <footer className="mba-pagination">
                    <button
                      className="page-btn"
                      onClick={() => loadProjects(Math.max(1, page - 1))}
                      disabled={page === 1}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <div className="page-info">
                      Page <strong>{page}</strong> of <strong>{pages}</strong>
                    </div>
                    <button
                      className="page-btn"
                      onClick={() => loadProjects(Math.min(pages, page + 1))}
                      disabled={page >= pages}
                    >
                      Next <FaArrowRight />
                    </button>
                  </footer>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMbaProjects;
